import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import LazyImage from '../components/ui/LazyImage';
import ContactOptionsModal from '../components/ContactOptionsModal';

gsap.registerPlugin(ScrollTrigger);

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
}

const About: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentAdvantageIndex, setCurrentAdvantageIndex] = useState(0);
  const [currentFishTestIndex, setCurrentFishTestIndex] = useState(0);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isContactsExpanded, setIsContactsExpanded] = useState(false);
  
  // Для свайпов на мобильных
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const companyRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const advantagesRef = useRef<HTMLDivElement>(null);
  const contactsRef = useRef<HTMLDivElement>(null);

  const galleryImages: GalleryImage[] = [
    { id: 1, src: '/assets/images/about/production_equipment.webp', alt: 'Производственное оборудование' },
    { id: 2, src: '/assets/images/about/recycling_shop.webp', alt: 'Цех переработки' },
    { id: 3, src: '/assets/images/about/quality-control.webp', alt: 'Система контроля качества' },
    { id: 4, src: '/assets/images/about/final_products.webp', alt: 'Готовая продукция' },
    { id: 5, src: '/assets/images/about/gown_zone.webp', alt: 'Зона роста' },
    { id: 6, src: '/assets/images/about/insectarium.webp', alt: 'Инсектарии' },
  ];

  const advantages = [
    {
      icon: '♻',
      title: 'Экологичность',
      description: 'Полная переработка органики'
    },
    {
      icon: '🌱',
      title: 'Плодородие',
      description: 'Улучшение качества почвы'
    },
    {
      icon: '🔬',
      title: 'Научный подход',
      description: 'Исследования и тестирования'
    },
    {
      icon: '💚',
      title: 'Здоровье',
      description: 'Безопасные продукты для людей и животных'
    }
  ];

  const nextAdvantage = () => {
    setCurrentAdvantageIndex((prev) => (prev + 1) % advantages.length);
  };

  const prevAdvantage = () => {
    setCurrentAdvantageIndex((prev) => (prev - 1 + advantages.length) % advantages.length);
  };

  const fishTestImages = [
    {
      id: 1,
      src: '/assets/images/about/pond_fish_testing.webp',
      title: 'Фото пруда с рыбой',
      description: 'Тестирование прикормки в действии'
    },
    {
      id: 2,
      src: '/assets/images/about/testing_process.webp',
      title: 'Процесс тестирования',
      description: 'Наблюдение за поведением рыбы'
    }
  ];

  const nextFishTest = () => {
    setCurrentFishTestIndex((prev) => (prev + 1) % fishTestImages.length);
  };

  const prevFishTest = () => {
    setCurrentFishTestIndex((prev) => (prev - 1 + fishTestImages.length) % fishTestImages.length);
  };

  // Функции для навигации по галерее
  const openGalleryModal = (image: GalleryImage) => {
    const index = galleryImages.findIndex(img => img.id === image.id);
    setCurrentImageIndex(index);
    setSelectedImage(image);
  };

  const nextImage = () => {
    const newIndex = (currentImageIndex + 1) % galleryImages.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentImageIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  // Функции для свайпов
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextImage();
    }
    if (isRightSwipe) {
      prevImage();
    }
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  // Обработка клавиш для галереи
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImage) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prevImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextImage();
          break;
        case 'Escape':
          e.preventDefault();
          setSelectedImage(null);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentImageIndex]);

  useEffect(() => {
    // Hero анимация
    if (titleRef.current) {
      gsap.fromTo(titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out"
        }
      );
    }

    if (subtitleRef.current) {
      gsap.fromTo(subtitleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          delay: 0.3
        }
      );
    }

    if (buttonRef.current) {
      gsap.fromTo(buttonRef.current,
        { 
          opacity: 0, 
          y: 30, 
          scale: 0.9,
          rotationX: -15
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotationX: 0,
          duration: 1.2,
          ease: "back.out(1.7)",
          delay: 0.6
        }
      );
    }

    // Анимации при скролле
    const sections = [
      { ref: companyRef, trigger: '.company-section' },
      { ref: galleryRef, trigger: '.gallery-section' },
      { ref: advantagesRef, trigger: '.advantages-section' },
      { ref: contactsRef, trigger: '.contacts-section' }
    ];

    sections.forEach(({ ref, trigger }) => {
      if (ref.current) {
        gsap.fromTo(ref.current.children,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: 'power2.out',
            scrollTrigger: {
              trigger,
              start: 'top 80%',
              end: 'bottom 5%',
              toggleActions: 'play none none reverse',
              scrub: false,
              fastScrollEnd: true
            }
          }
        );
      }
    });

    // Анимация галереи с задержкой
    if (galleryRef.current) {
      const galleryItems = galleryRef.current.querySelectorAll('.gallery-item');
      gsap.fromTo(galleryItems,
        { opacity: 0, y: 30, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: '.gallery-section',
            start: 'top 80%',
            end: 'bottom 5%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }

    // Анимация преимуществ с задержкой
    if (advantagesRef.current) {
      const advantageItems = advantagesRef.current.querySelectorAll('.advantage-item');
      gsap.fromTo(advantageItems,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: '.advantages-section',
            start: 'top 80%',
            end: 'bottom 5%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-96 flex items-center justify-center overflow-hidden">
        {/* Фоновое изображение ячменного поля */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/images/about/barley-field.webp)'
          }}
        />
        
        {/* Затемнение для контраста с белым текстом */}
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 ref={titleRef} className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
            ООО «ПРОТЭКО.РУС»
          </h1>
          <p ref={subtitleRef} className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Единственный за Уралом производитель инновационной органической продукции<br />
            на основе продукта жизнедеятельности личинок мухи Чёрная львинка.
          </p>
          <button 
            ref={buttonRef}
            onClick={() => setIsContactModalOpen(true)}
            className="px-8 py-4 border border-white/30 text-white font-medium text-lg rounded-lg hover:bg-white/10 backdrop-blur-sm transition-all duration-500 ease-out transform hover:scale-105"
          >
            Связаться с нами
          </button>
        </div>
      </section>

      {/* Company Block */}
      <section ref={companyRef} className="company-section py-24 px-6 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8">
                О компании
              </h2>
              <div className="space-y-6 text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                <p>
                  Мы создаём новый ЭКО мир - чистые и эффективные удобрения для почвы, корма и прикормки для животных, полезные продукты для людей.
                </p>
                <p>
                  Все это помогает сохранять ваше здоровье и восстанавливать баланс экосистемы.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-[3/4] bg-gray-200 rounded-2xl overflow-hidden">
                <LazyImage 
                  src="/assets/images/about/company-equipment.webp" 
                  alt="Производственное оборудование"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Production Gallery */}
      <section ref={galleryRef} className="gallery-section py-24 px-6 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-16 text-center">
            Галерея производства
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div
                key={image.id}
                className="gallery-item group"
              >
                <div
                  className="aspect-square bg-gray-200 rounded-2xl overflow-hidden cursor-pointer mb-3"
                  onClick={() => openGalleryModal(image)}
                >
                  <LazyImage 
                    src={image.src} 
                    alt={image.alt}
                    className="w-full h-full object-cover transition-all duration-300 group-hover:scale-110"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 dark:text-gray-400 font-medium">
                  {image.alt}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fish Testing Block */}
      <section className="py-24 px-6 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8">
            Тестирование прикормки для рыбы
          </h2>
          <div className="space-y-6 text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
            <p>
              Мы тестируем нашу прикормку для рыбы прямо на собственной базе в специально оборудованном пруду.
            </p>
            <p>
              Здесь проводятся испытания эффективности наших органических добавок, которые не только привлекают рыбу, но и способствуют улучшению качества воды в водоеме.
            </p>
          </div>
          
          {/* Fish test photos slider */}
          <div className="mt-12 relative max-w-2xl mx-auto">
            <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-2xl relative overflow-hidden">
              <LazyImage 
                src={fishTestImages[currentFishTestIndex].src} 
                alt={fishTestImages[currentFishTestIndex].title}
                className="w-full h-full object-cover"
              />
              
              {/* Overlay с информацией */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <p className="text-white text-lg font-medium mb-1">{fishTestImages[currentFishTestIndex].title}</p>
                <p className="text-white/90 text-sm">{fishTestImages[currentFishTestIndex].description}</p>
              </div>

              {/* Стрелка влево - показывается только если это не первая фотка */}
              {currentFishTestIndex > 0 && (
                <button
                  onClick={prevFishTest}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                  aria-label="Предыдущее фото"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                </button>
              )}

              {/* Стрелка вправо - показывается только если это не последняя фотка */}
              {currentFishTestIndex < fishTestImages.length - 1 && (
                <button
                  onClick={nextFishTest}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                  aria-label="Следующее фото"
                >
                  <ChevronRight className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                </button>
              )}
            </div>

            {/* Индикаторы слайдов */}
            <div className="flex justify-center space-x-2 mt-6">
              {fishTestImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentFishTestIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentFishTestIndex
                      ? 'bg-primary-600 dark:bg-primary-400'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }`}
                  aria-label={`Перейти к фото ${index + 1}`}
                />
              ))}
            </div>
          </div>
          
          <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl">
            <p className="text-blue-700 dark:text-blue-300 font-medium">
              💡 <strong>Результаты тестирования:</strong> Наша органическая прикормка показывает отличные результаты по привлечению рыбы и экологической безопасности
            </p>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section ref={advantagesRef} className="advantages-section py-24 px-6 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-16 text-center">
            Наши преимущества
          </h2>
          
          {/* Десктопная версия - сетка */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {advantages.map((advantage, index) => (
              <div
                key={advantage.title}
                className="advantage-item text-center"
              >
                <div className="text-6xl mb-6">{advantage.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {advantage.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {advantage.description}
                </p>
              </div>
            ))}
          </div>

          {/* Мобильная версия - слайдер */}
          <div className="md:hidden">
            <div className="relative">
              {/* Карточка преимущества */}
              <div className="advantage-item text-center px-4">
                <div className="text-6xl mb-6">{advantages[currentAdvantageIndex].icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {advantages[currentAdvantageIndex].title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {advantages[currentAdvantageIndex].description}
                </p>
              </div>

              {/* Стрелки навигации */}
              <button
                onClick={prevAdvantage}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-3 bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                aria-label="Предыдущее преимущество"
              >
                <ChevronLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </button>

              <button
                onClick={nextAdvantage}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-3 bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                aria-label="Следующее преимущество"
              >
                <ChevronRight className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            {/* Индикаторы слайдов */}
            <div className="flex justify-center space-x-2 mt-8">
              {advantages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentAdvantageIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentAdvantageIndex
                      ? 'bg-primary-600 dark:bg-primary-400'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }`}
                  aria-label={`Перейти к преимуществу ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contacts */}
      <section ref={contactsRef} className="contacts-section pt-8 pb-24 px-6 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-12">
            Контактная информация
          </h2>
          
          <div className="mb-8">
            {/* Мобильная версия - сворачиваемая */}
            <div className="md:hidden">
              <div className="space-y-3 text-lg text-gray-700 dark:text-white leading-relaxed">
                <p>
                  <strong>ООО «ПРОТЭКО.РУС»</strong>
                </p>
                <p>
                  <strong>Email:</strong> proteko.rus@mail.ru
                </p>
                
                {isContactsExpanded && (
                  <div className="space-y-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                    <p>
                      <strong>Юридический адрес:</strong><br />
                      646070, Омская область, Москаленский муниципальный район,<br />
                      г. п. Москаленское, рп. Москаленки,<br />
                      ул. Железнодорожная, д. 11
                    </p>
                    <p>
                      <strong>Время работы:</strong><br />
                      С нами можно связаться ежедневно с 9:00 до 21:00
                    </p>
                    <p>
                      <strong>ОГРН:</strong> 1225500020866
                    </p>
                    <p>
                      <strong>ИНН:</strong> 5528051933
                    </p>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setIsContactsExpanded(!isContactsExpanded)}
                className="mt-4 w-full flex items-center justify-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                <span className="text-sm font-medium">
                  {isContactsExpanded ? 'Скрыть подробности' : 'Показать подробности'}
                </span>
                {isContactsExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Десктопная версия - всегда развернутая */}
            <div className="hidden md:grid md:grid-cols-3 gap-8 text-lg text-gray-700 dark:text-white leading-relaxed">
              <div className="space-y-3">
                <p>
                  <strong>Юридический адрес:</strong><br />
                  646070, Омская область, Москаленский муниципальный район,<br />
                  г. п. Москаленское, рп. Москаленки,<br />
                  ул. Железнодорожная, д. 11
                </p>
              </div>
              <div className="space-y-3">
                <p>
                  <strong>ООО «ПРОТЭКО.РУС»</strong>
                </p>
                <p>
                  <strong>ОГРН:</strong> 1225500020866
                </p>
                <p>
                  <strong>ИНН:</strong> 5528051933
                </p>
              </div>
              <div className="space-y-3">
                <p>
                  <strong>Время работы:</strong><br />
                  С нами можно связаться ежедневно с 9:00 до 21:00
                </p>
                <p>
                  <strong>Email:</strong><br />
                  proteko.rus@mail.ru
                </p>
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => window.open('https://yandex.com/maps/-/CHttYA5V', '_blank')}
            className="btn-secondary dark:!bg-transparent dark:hover:!bg-gray-800 dark:!text-primary-400 dark:!border-primary-400 px-8 py-4 text-lg"
          >
            Построить маршрут
          </button>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative max-w-4xl max-h-full w-full"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <LazyImage 
              key={selectedImage.id}
              src={selectedImage.src} 
              alt={selectedImage.alt}
              className="w-full h-auto rounded-2xl transition-opacity duration-300"
            />
            
            {/* Кнопка закрытия */}
            <button
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors z-10"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>

            {/* Стрелка влево */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 p-2 md:p-3 bg-black/50 hover:bg-black/70 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 z-10"
              aria-label="Предыдущее фото"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            {/* Стрелка вправо */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-2 md:p-3 bg-black/50 hover:bg-black/70 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 z-10"
              aria-label="Следующее фото"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            {/* Информация о фото */}
            <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 text-center z-10 w-full max-w-md">
              <p className="text-white text-lg font-medium px-4 py-2 rounded-lg mb-2">
                {selectedImage.alt}
              </p>
              <p className="text-white/80 text-sm mb-3">
                {currentImageIndex + 1} из {galleryImages.length}
              </p>
              
              {/* Индикатор текущего фото */}
              <div className="flex justify-center space-x-2">
                {galleryImages.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      index === currentImageIndex
                        ? 'bg-white'
                        : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Подсказка о свайпах для мобильных */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center z-10">
              <div className="hidden md:block">
                <p className="text-white/60 text-xs">
                  Используйте стрелки или клавиши ← →
                </p>
              </div>
              <div className="md:hidden">
                <p className="text-white/60 text-xs">
                  Свайп влево/вправо для навигации
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <ContactOptionsModal
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        subtitle="Напишите или позвоните нам напрямую. Сайт не сохраняет ваши контактные данные."
      />
    </div>
  );
};

export default About;
